import { Modal } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { LANGUAGE_CODES } from "models";
import { ArticleForm } from "modules/NewsDashboard/components/ArticleModal/ArticleForm";
import { useCreateArticleMutation, useUpdateArticleMutation } from "modules/NewsDashboard/components/ArticleModal/hooks";

import type { Article, DealerLocation } from "models";
import type { CreateArticleFormValues } from "modules/NewsDashboard/components/ArticleModal/ArticleForm";

type ArticleModalProps = {
  isOpen: boolean;
  article: Article | null;
  onClose: () => void;
  refetchArticles: () => void;
};

const useGetDefaultCreateArticleValues = (location?: DealerLocation | null): CreateArticleFormValues => ({
  title: "",
  category_id: null,
  language_code: location?.language_code ?? LANGUAGE_CODES.English,
  publish_date: null,
  expire_date: null,
  content: "",
  selector: ".App",
  visible_on_tablet: true,
  visible_to_all: false,
  dealer_ids: [],
  location_ids: []
});

const FormHelper = createFormHelper<CreateArticleFormValues>();

export const ArticleModal = ({ article, isOpen, onClose, refetchArticles }: ArticleModalProps) => {
  const isEdit = Boolean(article);

  const createArticle = useCreateArticleMutation();
  const updateArticle = useUpdateArticleMutation();
  const { selectedLocation } = useSelectedLocation();

  const { isPending, validateArticle, hasValidationErrors, mutation } = isEdit
    ? { ...updateArticle, mutation: updateArticle.updateArticleMutation }
    : { ...createArticle, mutation: createArticle.createArticleMutation };

  const defaultValues = article
    ? {
        ...article,
        category_id: article.category_id || null,
        dealer_ids: article.dealer_ids || [],
        location_ids: article.location_ids || []
      }
    : useGetDefaultCreateArticleValues(selectedLocation);

  const handleSubmit = async (formData: CreateArticleFormValues) => {
    if (validateArticle(formData)) return;

    const payload = isEdit ? { ...formData, id: article?.id, category_id: article?.category_id || null } : formData;
    await mutation(payload as Article);

    onClose();
    refetchArticles();
  };

  return (
    <Modal open={isOpen} size="large" className="ArticleModal">
      <FormHelper.Form onSubmit={handleSubmit} defaultValues={defaultValues}>
        <ArticleForm onClose={onClose} isPending={isPending} hasValidationErrors={hasValidationErrors} article={article} refetchArticles={refetchArticles} />
      </FormHelper.Form>
    </Modal>
  );
};
